import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getCurrentUser,
  fetchAuthSession,
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut
} from 'aws-amplify/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 *
 * Manages authentication state for the entire application.
 * Supports three modes:
 * - Development: Mock auth, full access (REACT_APP_DEV_MODE=true)
 * - Debug: Cognito test users (REACT_APP_DEBUG_MODE=true)
 * - Production: Full Cognito OAuth + JWT (default)
 */
export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tier, setTier] = useState('free');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const devMode = process.env.REACT_APP_DEV_MODE === 'true';
  const debugMode = process.env.REACT_APP_DEBUG_MODE === 'true';
  const isLocal = process.env.REACT_APP_API_ENV === 'local' || devMode;

  const mode = devMode ? 'development' : (debugMode ? 'debug' : 'production');

  const getAccessToken = useCallback(async () => {
    if (devMode) return null;
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }, [devMode]);

  const fetchUserTier = useCallback(async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const headers = {};

      if (devMode) {
        headers['X-User-Id'] = userId;
      } else {
        const token = await getAccessToken();
        if (!token) return;
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${apiUrl}/profile`, { headers });

      if (response.ok) {
        const profile = await response.json();
        setTier(profile.tier || 'free');
        setSubscriptionStatus(profile.subscription_status);
      }
    } catch (error) {
      console.error('Error fetching user tier:', error);
      setTier('free');
    }
  }, [devMode, getAccessToken]);

  const checkAuth = useCallback(async () => {
    if (devMode) {
      const storedUserId = localStorage.getItem('user_id') || 'dev-user-123';
      setUser({
        id: storedUserId,
        name: 'Dev User',
        email: 'dev@example.com'
      });
      localStorage.setItem('user_id', storedUserId);
      setTier('family');
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }

    try {
      const cognitoUser = await getCurrentUser();

      // Use ID token claims directly — works for all auth methods (OAuth and email/password).
      // fetchUserAttributes() requires aws.cognito.signin.user.admin scope which is absent
      // for email/password tokens, causing a 400 error. Token claims are always available.
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload || {};
      const attributes = {
        sub: payload.sub,
        email: payload.email,
        name: payload.name || payload['cognito:username'],
        picture: payload.picture,
      };

      const userData = {
        id: attributes.sub || cognitoUser.userId,
        name: attributes.name || attributes.email?.split('@')[0] || cognitoUser.username,
        email: attributes.email,
        picture: attributes.picture
      };

      localStorage.setItem('user_id', userData.id);
      setUser(userData);
      setIsAuthenticated(true);

      await fetchUserTier(userData.id);
    } catch (error) {
      console.log('Not authenticated:', error);
      localStorage.removeItem('user_id');
      setUser(null);
      setTier('free');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [devMode, fetchUserTier]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const signIn = async (email, password) => {
    if (isLocal) {
      await checkAuth();
      return;
    }

    try {
      await amplifySignIn({ username: email, password });
    } catch (err) {
      if (err.name === 'UserAlreadyAuthenticatedException') {
        // Already signed in (e.g. from a previous verify-email flow) — just refresh state
      } else {
        throw err;
      }
    }
    await checkAuth();
  };

  const signUp = async (email, password, name) => {
    if (isLocal) return;

    await amplifySignUp({
      username: email,
      password,
      options: {
        userAttributes: { email, name }
      }
    });
  };

  const signOut = async () => {
    if (isLocal) {
      localStorage.removeItem('user_id');
      setUser(null);
      setIsAuthenticated(false);
      return;
    }

    await amplifySignOut();
    localStorage.removeItem('user_id');
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    tier,
    subscriptionStatus,
    mode,
    isLocal,
    devMode,
    debugMode,
    signIn,
    signUp,
    signOut,
    checkAuth,
    getAccessToken,
    refreshTier: () => user && fetchUserTier(user.id)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
